<template>
  <div class="max-w-6xl h-full m-auto py-4 px-4 flex flex-col space-y-4">
    <component
      :is="slideoverComponent"
      :title="slideoverTitle"
      :visible="slideoverVisible"
      v-bind="slideoverProperties"
      @toggle-slideover="toggleSlideover"
      @refresh-orders="refreshOrders"
    />
    <div class="flex flex-row justify-between space-x-4 py-2">
      <h2 class="text-2xl">Unprocessed Orders Report</h2>

      <div class="flex space-x-4 items-center">
        <Button
          v-if="userCanProcess"
          :disabled="isLoading || !hasSelectedItem"
          v-on:click-handler="
            toggleSlideover('ProcessOrder', 'Process Order', {
              itemToBeProcessed: selectedItem,
            })
          "
          class="btn btn-primary btn-small flex space-x-2 items-center"
        >
          <span>Process Order</span>
          <LoadingSpinner v-if="isLoading" class="h-5 w-5" />
        </Button>
        <Button
          v-on:click-handler="loadReport()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading"
        >
          <span>Refresh Report</span>
          <LoadingSpinner v-if="isLoading" class="h-5 w-5" />
        </Button>
      </div>
    </div>

    <ag-grid-vue
      style="height: 100%; width: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="unprocessed-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      @firstDataRendered="onFirstDataRendered"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import OrderLink from "./components/OrderLink";
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button";
import dateFormatter from "@/mixins/dateFormatter";
import auth from "@/api/auth";
import ProcessOrder from "@/views/Reports/components/slideovers/OrderEdit/ProcessOrder";

const Reports = ApiFactory.get("reports");

export default {
  name: "UnprocessedOrders",
  components: {
    Button,
    AgGridVue,
    ProcessOrder,
    OrderLink, // eslint-disable-line
  },
  mixins: [dateFormatter],
  props: {},
  data() {
    return {
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      isLoading: false,
      selectedItem: {},
      slideoverTitle: "",
      slideoverVisible: false,
      slideoverComponent: null,
      slideoverProperties: {},
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    },
    hasSelectedItem() {
      return Boolean(this.selectedItem.SourceOrderNumber);
    },
    userCanProcess() {
      return auth.isUserAuthorized(["admin"]);
    },
  },
  beforeMount() {
    this.gridOptions = {
      headerHeight: 60,
      onSelectionChanged: this.itemSelectionChanged,
      rowSelection: "single",
      rowDeselection: true,
    };

    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      suppressMovable: true,
      resizable: true,
    };

    this.columnDefs = [
      {
        headerName: "Order #",
        field: "SourceOrderNumber",
        cellRendererFramework: "OrderLink",
        cellRendererParams: {
          target: "_blank",
        },
      },
      {
        headerName: "Status",
        field: "Status",
      },
      {
        headerName: "Order Date",
        field: "OrderDate",
        valueFormatter(params) {
          return dateFormatter.format(params.value);
        },
        filter: "agDateColumnFilter",
      },
    ];

    this.loadReport();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  methods: {
    toggleSlideover(component, title, properties) {
      if (component) {
        this.slideoverComponent = component;
        this.slideoverTitle = title;
        this.slideoverProperties = properties || {};
      }

      setTimeout(() => {
        this.slideoverVisible = !this.slideoverVisible;
      }, 3);
    },
    itemSelectionChanged() {
      let rows = this.gridOptions.api.getSelectedRows();
      if (rows.length !== 1) {
        this.selectedItem = {};
        return;
      }
      this.selectedItem = rows[0];
    },
    refreshOrders() {
      this.$router.go(0);
    },
    loadReport() {
      this.isLoading = true;
      this.gridOptions?.api?.showLoadingOverlay();

      Reports.getUnprocessedOrders()
        .then((response) => {
          this.rowData = response.data.body.map((data) => {
            let { OrderDate, ...properties } = data;

            return {
              ...properties,
              OrderDate: this.setDate(OrderDate),
            };
          });
        })
        .finally(() => (this.isLoading = false));
    },
    setDate(value) {
      if (!value) {
        return null;
      }

      return new Date(value);
    },
    exportCSV() {
      if (!this.gridHasData) return;
      this.gridApi.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date()),
      });
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {},
  },
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";

.ag-header-cell-label .ag-header-cell-text {
  text-align: center;
  white-space: normal !important;
}
.ag-row-hover {
  background-color: #f3faf7 !important;
  color: black !important;
}
</style>
